// @ts-strict-ignore
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { DateFormatter } from '@shared/date-formatter';
import { ArticleMetadataVO, ArticleTypeVO } from '@article/model/vo/article.vo';
import { AdminToolService } from '../../../services/admintool.service';
import { ArticleDataService } from '@article/service/article-data.service';
import { CustomValidators } from '@shared/custom-validators';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { AlertHelper } from '@shared/alert/alert.helper';
import { lastValueFrom } from 'rxjs';

/**
 * Component to set article data e.g. type, ean no., material status
 */
@Component({
  standalone: false,
  selector: 'app-manage-article-meta-info-modal',
  templateUrl: './manage-article-meta-info.component.html',
})
export class ManageArticleMetaInfoComponent implements OnInit {
  @Input() public articleId: number;
  @Output() public finish = new EventEmitter<ArticleMetadataVO>();
  @Input() public articleMetaData: ArticleMetadataVO;

  public form: FormGroup;
  public articleTypes: ArticleTypeVO[];
  public selectedArticleType: number;
  public isNewArticle: boolean;

  constructor(
    private fb: FormBuilder,
    private bsModalRef: BsModalRef,
    private articleDataService: ArticleDataService,
    private adminToolService: AdminToolService,
    private localeResourceManager: LocaleResourceManager,
    private alertHelper: AlertHelper
  ) {}

  ngOnInit() {
    this.isNewArticle = !!this.articleMetaData;
    this.form = this.fb.group({
      articleType: [{value: '', disabled: false}, { validators: this.validateArticleTypeInputRequired() }],
      articleId: [{value: '', disabled: !this.isNewArticle }, { validators: [CustomValidators.required, CustomValidators.integer] }],
      oldArticleId: [{ value: '', disabled: false }, { validators: CustomValidators.integer }],
      articleNo: [{value: '', disabled: !this.isNewArticle }, { validators: [CustomValidators.required, CustomValidators.trimRequired] }],
      eanNo: [{ value: '', disabled: false }, { validators: CustomValidators.integer }],
      materialStatus: [{ value: '', disabled: false }, { validators: CustomValidators.integer }],
      lastCall: [{ value: '', disabled: true }],
      publicGlobal: [{ value: '', disabled: false }],
      minimumStockLevel: [{ value: '', disabled: false }, { validators: CustomValidators.integer }],
      isFullArticle: [{ value: '', disabled: false }],
    });

    if (!this.isNewArticle) {
      this.adminToolService.getArticleMetadata(this.articleId).subscribe((metaData: ArticleMetadataVO) => {
        this.articleMetaData = metaData;
        this.setInitialData();
      });
    } else {
      this.setInitialData();
    }
    this.articleDataService.getAllArticleTypes().subscribe((types: ArticleTypeVO[]) => {
      types.sort((a, b) => {
        return a.id - b.id;
      });
      this.articleTypes = types;
    });
  }

  private setInitialData(): void {
    this.selectedArticleType = this.articleMetaData.articleType.id;
    this.form.controls.articleId.setValue(this.articleMetaData.articleId);
    this.form.controls.articleNo.setValue(this.articleMetaData.articleIdMapping.articleNo);
    this.form.controls.eanNo.setValue(this.articleMetaData.articleIdMapping.eanNo);
    this.form.controls.materialStatus.setValue(this.articleMetaData.articleIdMapping.materialStatus);
    this.form.controls.lastCall.setValue(DateFormatter.formatShortDate(this.articleMetaData.articleIdMapping.lastCall));
    this.form.controls.minimumStockLevel.setValue(this.articleMetaData.articleIdMapping.minimumStockLevel);
    this.form.controls.oldArticleId.setValue(this.articleMetaData.articleIdMapping.oldArticleId);
    this.form.controls.isFullArticle.setValue(this.articleMetaData.articleIdMapping.isFullArticle);
  }

  private setArticleType(): void {
    const chosenType: ArticleTypeVO = this.articleTypes.find((type: ArticleTypeVO) => {
      return type.id === this.selectedArticleType;
    });
    this.articleMetaData.articleType = chosenType;
  }

  private validateArticleTypeInputRequired(): ValidatorFn {
    return (c: FormControl) => {
      return c.value || c.value === 0 ? null : {messageKey: 'validation.error.required_field'};
    };
  }

  public getInputError(c: AbstractControl): string {
    if (c && c.hasError('messageKey')) {
      return this.localeResourceManager.getValueByKey(c.getError('messageKey'), { 0: c.getError('param') });
    } else {
      return '';
    }
  }

  public close(metaData: ArticleMetadataVO = null): void {
    this.finish.emit(metaData);
    this.bsModalRef.hide();
  }

  public async save(): Promise<void> {
    if (this.form.valid) {
      this.setArticleType();
      this.articleMetaData.articleId = Number(this.form.controls.articleId.value);
      this.articleMetaData.articleIdMapping.articleNo = this.form.controls.articleNo.value;
      this.articleMetaData.articleIdMapping.eanNo = Number(this.form.controls.eanNo.value);
      this.articleMetaData.articleIdMapping.materialStatus = Number(this.form.controls.materialStatus.value);
      this.articleMetaData.articleIdMapping.lastCall = DateFormatter.parseShortDate(this.form.controls.lastCall.value);
      this.articleMetaData.articleIdMapping.minimumStockLevel = Number(this.form.controls.minimumStockLevel.value);
      this.articleMetaData.articleIdMapping.oldArticleId = Number(this.form.controls.oldArticleId.value);
      this.articleMetaData.articleIdMapping.isFullArticle = this.form.controls.isFullArticle.value;

      if (this.isNewArticle) {
        const foundArticle = await lastValueFrom(this.articleDataService.loadArticleByArticleId(this.articleMetaData.articleId));
        if (foundArticle) {
          this.alertHelper.showError('manageArticleMetaInfo.error.articleExist.title', 'manageArticleMetaInfo.error.articleExist.message');
        } else {
          this.close(this.articleMetaData);
        }
      } else {
        const metaData = await lastValueFrom(this.adminToolService.updateArticleMetadata(this.articleMetaData));
        this.close(metaData);
      }
    }
  }
}
